/**
 * AWS Cognito Authentication Service
 *
 * This service provides authentication functionality using AWS Cognito User Pools.
 * It handles sign-in, sign-out, token management, and session persistence.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import { cognitoConfig } from '../config/cognito';

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.clientId,
});

// JWT payload interface
interface CognitoJwtPayload {
  sub: string;
  email: string;
  'cognito:groups'?: string[];
  exp: number;
  iat: number;
}

// User info interface
export interface UserInfo {
  userId: string;  // Cognito sub
  email: string;
  username: string;
  groups: string[];
  isAdmin: boolean;
}

/**
 * Sign in user with email and password
 */
export const signIn = (email: string, password: string): Promise<{ user: UserInfo; accessToken: string; idToken: string; needsPasswordChange?: boolean }> => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const payload = jwtDecode<CognitoJwtPayload>(idToken);

        const userInfo: UserInfo = {
          userId: payload.sub,
          email: payload.email,
          username: payload.email, // Use email as username
          groups: payload['cognito:groups'] || [],
          isAdmin: (payload['cognito:groups'] || []).includes('admin'),
        };

        resolve({ user: userInfo, accessToken, idToken });
      },
      onFailure: (err) => {
        console.error('Authentication failed:', err);
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User needs to set a new password (first login with temp password)
        console.log('New password required', userAttributes, requiredAttributes);
        reject({
          code: 'NewPasswordRequired',
          message: 'New password required. Please change your password.',
          userAttributes,
          requiredAttributes,
          cognitoUser, // Pass cognitoUser for password change flow
        });
      },
    });
  });
};

/**
 * Complete new password challenge (for first-time login)
 */
export const completeNewPassword = (
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: any
): Promise<{ user: UserInfo; accessToken: string; idToken: string }> => {
  return new Promise((resolve, reject) => {
    // Remove attributes that are already set or shouldn't be updated
    // Email cannot be modified as it's already provided
    delete userAttributes.email;
    delete userAttributes.email_verified;
    delete userAttributes.phone_number_verified;

    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      userAttributes,
      {
        onSuccess: (session: CognitoUserSession) => {
          const idToken = session.getIdToken().getJwtToken();
          const accessToken = session.getAccessToken().getJwtToken();
          const payload = jwtDecode<CognitoJwtPayload>(idToken);

          const userInfo: UserInfo = {
            userId: payload.sub,
            email: payload.email,
            username: payload.email,
            groups: payload['cognito:groups'] || [],
            isAdmin: (payload['cognito:groups'] || []).includes('admin'),
          };

          resolve({ user: userInfo, accessToken, idToken });
        },
        onFailure: (err) => {
          console.error('Password change failed:', err);
          reject(err);
        },
      }
    );
  });
};

/**
 * Sign out current user
 */
export const signOut = (): Promise<void> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    resolve();
  });
};

/**
 * Logout from Hosted UI and redirect to Cognito logout endpoint
 *
 * Note: localStorage should be cleared BEFORE calling this function
 * to avoid race conditions during redirect
 */
export const logoutFromHostedUI = (): void => {
  const domain = cognitoConfig.hostedUIDomain;
  const clientId = cognitoConfig.clientId;

  // Cognito logout endpoint - will clear session and redirect to logout_uri
  // IMPORTANT: The parameter must be 'logout_uri' not 'redirect_uri'
  const logoutUri = encodeURIComponent(window.location.origin + '/logout');
  const logoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;

  console.log('🚪 Redirecting to Cognito logout:', {
    domain,
    clientId,
    logoutUri: window.location.origin + '/logout',
    fullUrl: logoutUrl
  });

  // Directly redirect to logout page since Cognito logout doesn't always honor logout_uri
  // Clear the session by calling Cognito, but immediately redirect to our logout page
  window.location.replace(logoutUrl);
};

/**
 * Get current session (returns tokens if valid, null if expired or not authenticated)
 */
export const getCurrentSession = (): Promise<CognitoUserSession | null> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        console.error('Failed to get session:', err);
        resolve(null);
        return;
      }

      if (session && session.isValid()) {
        resolve(session);
      } else {
        resolve(null);
      }
    });
  });
};

/**
 * Get access token (with automatic refresh if needed)
 * Works with both SDK authentication and Hosted UI/Managed Login authentication
 */
export const getAccessToken = async (): Promise<string | null> => {
  try {
    // First, try to get from localStorage (OAuth flow)
    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      return storedToken;
    }

    // Fallback to SDK session (for custom login flow, if ever used)
    const session = await getCurrentSession();
    if (session) {
      return session.getAccessToken().getJwtToken();
    }

    return null;
  } catch (error) {
    console.error('Failed to get access token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired or about to expire (within 5 minutes)
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<CognitoJwtPayload>(token);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    const isExpired = currentTime >= (expirationTime - bufferTime);

    return isExpired;
  } catch (error) {
    console.error('[cognitoAuth] Failed to decode token:', error);
    return true; // Treat invalid tokens as expired
  }
};

/**
 * Get ID token (with automatic refresh if needed)
 * Works with both SDK authentication and Hosted UI/Managed Login authentication
 */
export const getIdToken = async (): Promise<string | null> => {
  try {
    // First, try to get from localStorage (OAuth flow)
    const storedToken = localStorage.getItem('id_token');
    if (storedToken) {
      // Check if token is expired or about to expire
      if (isTokenExpired(storedToken)) {
        // Try to refresh the session
        try {
          const newSession = await refreshSession();
          const newToken = newSession.getIdToken().getJwtToken();
          // Update localStorage with new tokens
          localStorage.setItem('id_token', newToken);
          localStorage.setItem('access_token', newSession.getAccessToken().getJwtToken());
          return newToken;
        } catch (refreshError) {
          console.error('[cognitoAuth] Failed to refresh session:', refreshError);
          // Clear expired tokens and force re-login
          localStorage.removeItem('id_token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          return null;
        }
      }
      return storedToken;
    }

    // Fallback to SDK session (for custom login flow, if ever used)
    const session = await getCurrentSession();
    if (session) {
      const token = session.getIdToken().getJwtToken();
      // Check expiration for SDK session too
      if (isTokenExpired(token)) {
        const newSession = await refreshSession();
        return newSession.getIdToken().getJwtToken();
      }
      return token;
    }

    return null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
};

/**
 * Get current user info from session
 */
export const getCurrentUser = async (): Promise<UserInfo | null> => {
  try {
    const session = await getCurrentSession();
    if (!session) {
      return null;
    }

    const idToken = session.getIdToken().getJwtToken();
    const payload = jwtDecode<CognitoJwtPayload>(idToken);

    return {
      userId: payload.sub,
      email: payload.email,
      username: payload.email,
      groups: payload['cognito:groups'] || [],
      isAdmin: (payload['cognito:groups'] || []).includes('admin'),
    };
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const session = await getCurrentSession();
    return session !== null && session.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * Refresh session tokens
 * Works with both SDK authentication and OAuth/Hosted UI
 */
export const refreshSession = async (): Promise<CognitoUserSession> => {
  // First, check if we're using OAuth flow (tokens in localStorage)
  const storedRefreshToken = localStorage.getItem('refresh_token');

  if (storedRefreshToken) {
    // OAuth flow - need to call Cognito token endpoint
    try {
      const response = await fetch(`${cognitoConfig.hostedUIDomain}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: cognitoConfig.clientId,
          refresh_token: storedRefreshToken,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[cognitoAuth] Token refresh failed:', errorText);
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();

      // Update localStorage with new tokens
      localStorage.setItem('id_token', data.id_token);
      localStorage.setItem('access_token', data.access_token);

      // Return a mock CognitoUserSession for compatibility
      // We only need the getIdToken() method to work
      return {
        getIdToken: () => ({ getJwtToken: () => data.id_token }),
        getAccessToken: () => ({ getJwtToken: () => data.access_token }),
        isValid: () => true,
      } as any;

    } catch (error) {
      console.error('[cognitoAuth] OAuth token refresh failed:', error);
      throw error;
    }
  }

  // Fallback to SDK session refresh
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user found'));
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }

      if (!session) {
        reject(new Error('No session found'));
        return;
      }

      const refreshToken = session.getRefreshToken();
      cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          reject(refreshErr);
          return;
        }
        resolve(newSession);
      });
    });
  });
};

/**
 * Change user password
 * Works with both SDK authentication and Hosted UI/Managed Login authentication
 */
export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  try {
    // Get access token from localStorage (set during OAuth flow)
    const accessToken = localStorage.getItem('access_token');

    if (!accessToken) {
      throw new Error('No access token found. Please log in again.');
    }

    // Use Cognito Identity Provider API to change password
    const response = await fetch(`https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
      },
      body: JSON.stringify({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.__type || 'Failed to change password');
    }

    return;
  } catch (error: any) {
    console.error('Change password error:', error);
    throw error;
  }
};

/**
 * Get user attributes
 */
export const getUserAttributes = (): Promise<Record<string, string>> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user found'));
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error('No session'));
        return;
      }

      cognitoUser.getUserAttributes((attrErr, attributes) => {
        if (attrErr) {
          reject(attrErr);
          return;
        }

        const attributeMap: Record<string, string> = {};
        attributes?.forEach((attr) => {
          attributeMap[attr.getName()] = attr.getValue();
        });

        resolve(attributeMap);
      });
    });
  });
};

/**
 * Build Cognito Hosted UI URL and redirect
 * Uses /oauth2/authorize endpoint which redirects to Managed Login or Hosted UI based on branding version
 */
export const redirectToHostedUI = (): void => {
  const domain = cognitoConfig.hostedUIDomain;
  const clientId = cognitoConfig.clientId;
  const redirectUri = encodeURIComponent(window.location.origin + '/callback');
  const responseType = 'code';
  // Include aws.cognito.signin.user.admin scope for user operations like password change
  const scope = encodeURIComponent('email openid profile aws.cognito.signin.user.admin');

  // Use /oauth2/authorize endpoint - Amazon Cognito will redirect to the appropriate UI based on branding version
  const hostedUIUrl = `https://${domain}/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}`;

  console.log('Redirecting to Login UI:', hostedUIUrl);
  window.location.href = hostedUIUrl;
};

/**
 * Parse OAuth callback URL to extract authorization code
 */
export const parseOAuthCallback = (): { code: string | null; error: string | null } => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');

  if (error) {
    console.error('OAuth error:', error, errorDescription);
    return { code: null, error: errorDescription || error };
  }

  return { code, error: null };
};

/**
 * Exchange authorization code for tokens
 */
export const exchangeCodeForTokens = async (code: string): Promise<{ user: UserInfo; accessToken: string; idToken: string }> => {
  const domain = cognitoConfig.hostedUIDomain;
  const clientId = cognitoConfig.clientId;
  const redirectUri = window.location.origin + '/callback';

  const tokenEndpoint = `https://${domain}/oauth2/token`;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: code,
    redirect_uri: redirectUri,
  });

  try {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error_description || 'Failed to exchange code for tokens');
    }

    const data = await response.json();
    const idToken = data.id_token;
    const accessToken = data.access_token;

    // Decode ID token to get user info
    const payload = jwtDecode<CognitoJwtPayload>(idToken);

    const userInfo: UserInfo = {
      userId: payload.sub,
      email: payload.email,
      username: payload.email,
      groups: payload['cognito:groups'] || [],
      isAdmin: (payload['cognito:groups'] || []).includes('admin'),
    };

    // Store tokens in localStorage for session persistence
    localStorage.setItem('id_token', idToken);
    localStorage.setItem('access_token', accessToken);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }

    return { user: userInfo, accessToken, idToken };
  } catch (error) {
    console.error('Token exchange failed:', error);
    throw error;
  }
};
